import React from "react";
import PropTypes from "prop-types";
import FaWarn from "react-icons/fa/exclamation-triangle";
import { getExportErrorText } from "utils/regex";
import styles from "components/documents/dropdown.scss";
import {
  categoryType,
  columnObjectType,
  paymentTypeType,
  currencyType,
  documentPropType
} from "custom-prop-types";
import { FormComponent, TaxCalculator } from "components/documents/documents-grid/dropdown-components";
import { Button } from "components/common/button";

export const ReceiptForm = (props) => {
  const formFields = props.columns.filter((column) => (
    !column.visible
    && (
      column.key === "vendor"
      || column.key === "issued"
      || column.key === "total"
      || column.key === "invoiceNumber"
      || column.key === "categories"
      || column.key === "currency"
    )
  ));

  return (
    <div>
      <div className={ styles.row }>
        {renderReceiptVariableFields(formFields, props)}
        <div className={ styles.flex }>
          <FormComponent
            field="tax"
            max={ null }
            onChange={ props.onUpdateDocument }
            placeholder="$0.00"
            title="Tax"
            type="number"
            value={ props.focusedDocument.tax }
          />
          <TaxCalculator
            change={ props.onUpdateDocument }
            currency={ props.focusedDocument.currency }
            preferredCurrency={ props.preferredCurrency }
            taxRate={ props.taxRate }
            total={ props.focusedDocument.total }
          />
        </div>
        <div className={ styles.flex }>
          <FormComponent
            field="currency"
            onChange={ props.onUpdateDocument }
            options={ mapCurrencies(props.currencies) }
            placeholder="Choose a currency"
            title="Currency"
            type="select"
            value={ cleanCurrencyValue(props.focusedDocument.currency) }
          />
        </div>
        <div className={ styles.flex }>
          <FormComponent
            field="paymentType"
            onChange={ props.onUpdatePaymentType }
            placeholder="Choose a payment type"
            title="Payment Type"
            type="paymentType"
            valueNumber={ props.focusedDocument.paymentType.lastFourDigits }
            valueType={ props.formPaymentType }
          />
        </div>
        <div className={ styles.flex }>
          <FormComponent
            field="notes"
            onChange={ props.onUpdateDocument }
            title="Notes"
            type="notes"
            value={ props.notes }
          />
        </div>
        <div className={ `${styles.flex} ${styles.categories}` }>
          <FormComponent
            accountId={ props.accountId }
            addFunction={ props.onCreateCategory }
            field="categories"
            onChange={ props.onUpdateDocument }
            options={ props.categories }
            placeholder="Add a category..."
            title="Categories"
            type="createable"
            value={ props.focusedDocument.categories }
          />
        </div>
      </div>
      {renderExportError(props.focusedDocument.notes)}
      <div className={ styles["actions-container"] }>
        <div className={ styles.cancel }>
          <Button link onClick={ () => props.onUnfocusDocument() }>cancel</Button>
        </div>
        <Button onClick={ () => { props.onSaveDocument(); props.onUnfocusDocument(); } } primary>Save</Button>
      </div>
    </div>
  );
};

export const renderExportError = (notes) => {
  const error = getExportErrorText(notes);
  if (error) {
    return (
      <div className={ styles["export-error"] }>
        <FaWarn className={ styles.warning } size={ 15 } />
        <span className={ styles["export-error-text"] }>Export Error</span>
        <div className={ styles["export-tooltip"] }>{error[1]}</div>
      </div>
    );
  }
  return null;
};

const renderReceiptVariableFields = (formFields, props) => formFields.map((field) => {
  switch (field.key) {
    case "vendor":
      return (
        <div className={ styles.flex } key={ field.key }>
          <FormComponent
            field="vendor"
            onChange={ props.onUpdateDocument }
            placeholder="Enter a vendor..."
            title="Vendor"
            type="text"
            value={ props.focusedDocument.vendor }
          />
        </div>
      );
    case "issued":
      return (
        <div className={ styles.flex } key={ field.key }>
          <FormComponent
            field="issued"
            onChange={ props.onUpdateDocument }
            title="Date"
            type="date"
            value={ props.focusedDocument.issued }
          />
        </div>
      );
    case "total":
      return (
        <div className={ styles.flex } key={ field.key }>
          <FormComponent
            field="total"
            max={ null }
            onChange={ props.onUpdateDocument }
            placeholder="$0.00"
            title="Total"
            type="number"
            value={ props.focusedDocument.total }
          />
        </div>
      );
    case "invoiceNumber":
      return (
        <div className={ styles.flex } key={ field.key }>
          <FormComponent
            field="invoiceNumber"
            onChange={ props.onUpdateDocument }
            placeholder="Enter an invoice number..."
            title="Invoice Number"
            type="text"
            value={ props.focusedDocument.invoiceNumber }
          />
        </div>
      );
    default:
      return null;
  }
});

ReceiptForm.defaultProps = {
  formPaymentType: { type: "" }
};

ReceiptForm.propTypes = {
  accountId: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(categoryType).isRequired,
  columns: PropTypes.arrayOf(columnObjectType).isRequired,
  currencies: PropTypes.arrayOf(currencyType).isRequired,
  focusedDocument: documentPropType.isRequired,
  formPaymentType: paymentTypeType,
  notes: PropTypes.string.isRequired,
  onCreateCategory: PropTypes.func.isRequired,
  onSaveDocument: PropTypes.func.isRequired,
  onUnfocusDocument: PropTypes.func.isRequired,
  onUpdateDocument: PropTypes.func.isRequired,
  onUpdatePaymentType: PropTypes.func.isRequired,
  preferredCurrency: PropTypes.string.isRequired,
  taxRate: PropTypes.number.isRequired
};

const mapCurrencies = (currencies) => currencies.map(currency => ({ key: currency.code, name: currency.code }));
const cleanCurrencyValue = (currency) => ({ key: currency, name: currency });

export const OtherDocumentForm = (props) => {
  const formFields = props.columns.filter((column) => (
    !column.visible && (column.key === "vendor" || column.key === "issued")
  ));
  return (
    <div>
      <div className={ styles.row }>
        {renderOtherDocumentVariableFields(formFields, props)}
        <div className={ styles["other-flex"] }>
          <FormComponent
            field="notes"
            onChange={ props.onUpdateDocument }
            title="Notes"
            type="notes"
            value={ props.focusedDocument.notes }
          />
        </div>
      </div>
      <div className={ styles["actions-container"] }>
        <div className={ styles.cancel }>
          <Button link onClick={ () => props.onUnfocusDocument() }>cancel</Button>
        </div>
        <Button onClick={ () => { props.onSaveDocument(); props.onUnfocusDocument(); } } primary>Save</Button>
      </div>
    </div>
  );
};

OtherDocumentForm.propTypes = {
  columns: PropTypes.arrayOf(columnObjectType).isRequired,
  focusedDocument: documentPropType.isRequired,
  onSaveDocument: PropTypes.func.isRequired,
  onUnfocusDocument: PropTypes.func.isRequired,
  onUpdateDocument: PropTypes.func.isRequired
};

const renderOtherDocumentVariableFields = (formFields, props) => formFields.map((field) => {
  switch (field.key) {
    case "issued":
      return (
        <div className={ styles["other-flex"] } key={ field.key }>
          <FormComponent
            field="issued"
            onChange={ props.onUpdateDocument }
            title="Date"
            type="date"
            value={ props.focusedDocument.issued }
          />
        </div>
      );
    case "vendor":
      return (
        <div className={ styles["other-flex"] } key={ field.key }>
          <FormComponent
            field="vendor"
            onChange={ props.onUpdateDocument }
            placeholder="Enter a name..."
            title="Name"
            type="text"
            value={ props.focusedDocument.vendor || props.focusedDocument.name }
          />
        </div>
      );
    default:
      return null;
  }
});



// WEBPACK FOOTER //
// ./src/js/components/documents/documents-grid/dropdown-forms.jsx